import { hasPath, insert, remove } from "ramda";
import React from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { Field } from "src/common/components/Field";
import {
	AddButton,
	AddFirstButton,
	FieldList,
	FieldListInput,
	FieldListItem,
	RemoveButton
} from "src/common/components/FieldList";
import { UnknownInputRenderer } from "src/features/forms/components/UnknownInputRenderer";
import { FormContext } from "src/features/forms/context/FormContext";
import { IsLockedContext } from "src/features/forms/context/IsLockedContext";
import {
	NodeContext,
	NodeContextProvider
} from "src/features/forms/context/NodeContext";
import {
	useError,
	useMaxItems,
	useMinItems,
	useRequired,
	useValue
} from "src/features/forms/hooks/useField";
import { ComplexFieldListData, UnknownInput } from "src/features/forms/types";

export const ComplexFieldListRenderer: React.FunctionComponent<ComplexFieldListData> =
	React.memo(props => (
		<NodeContextProvider path={props.path}>
			<ContentRenderer {...props} />
		</NodeContextProvider>
	));

const ContentRenderer: React.FunctionComponent<
	Pick<
		ComplexFieldListData,
		"label" | "help" | "errorMessages" | "inputs" | "path"
	>
> = React.memo(
	({ label: labelMsg, help: helpMsg, errorMessages, inputs, path }) => {
		const { formatMessage } = useIntl();

		const required = useRequired();

		const label = formatMessage(labelMsg);
		const help = helpMsg === undefined ? null : formatMessage(helpMsg);

		const isLocked = React.useContext(IsLockedContext);

		const minItems = useMinItems();
		const maxItems = useMaxItems();

		const [values, setValues] = useValue<unknown[]>([]);

		if (values.length === 0) {
			return (
				<Field required={required} label={`1. ${label}`} help={help}>
					<AddFirstButton
						disabled={isLocked}
						onClick={() => {
							setValues([{}]);
						}}
					/>
				</Field>
			);
		}

		return (
			<FieldList path={path}>
				{values.map((_, i) => (
					<NodeContextProvider key={i} path={[i]}>
						<ItemRenderer
							required={required}
							label={`${i + 1}. ${label}`}
							help={help}
							errorMessages={errorMessages}
						>
							{inputs.map(({ key, input }) => (
								<FieldListInput key={key}>
									<NodeContextProvider path={[key]}>
										<InputRenderer key={key} {...input} />
									</NodeContextProvider>
								</FieldListInput>
							))}
							<RemoveButton
								disabled={isLocked || values.length <= minItems}
								onClick={() => {
									setValues(remove(i, 1, values));
								}}
							/>
							<AddButton
								disabled={isLocked || values.length >= maxItems}
								onClick={() => {
									setValues(insert(i + 1, {}, values));
								}}
							/>
						</ItemRenderer>
					</NodeContextProvider>
				))}
			</FieldList>
		);
	}
);

const ItemRenderer: React.FunctionComponent<
	React.PropsWithChildren<{
		required: boolean;
		label: React.ReactNode;
		help: React.ReactNode;
		errorMessages: {
			[key: string]: MessageDescriptor;
		};
	}>
> = React.memo(({ required, label, help, errorMessages, children }) => {
	const error = useError(errorMessages);
	return (
		<FieldListItem>
			<Field required={required} label={label} help={help} error={error}>
				{children}
			</Field>
		</FieldListItem>
	);
});

const InputRenderer: React.FunctionComponent<UnknownInput> = input => {
	const { value } = React.useContext(FormContext);
	const { path } = React.useContext(NodeContext);
	if (!hasPath(path as string[], value)) {
		return null;
	}
	return <UnknownInputRenderer {...input} />;
};
